import { Skeleton } from "antd"
import React, { type ReactNode, Suspense, lazy } from "react"
import { type RouteObject } from "react-router-dom"

import {
  PermissionGate,
  type PermissionPath
} from "../../../../components/PermissionGate/PermissionGate.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import {
  COMMUNICATION_BULK_SEND_PERMISSION,
  COMMUNICATION_SEND_PERMISSION,
  COMMUNICATION_SETTINGS_PERMISSION,
  COMMUNICATION_TEMPLATES_PERMISSION,
  COMMUNICATION_VIEW_ALL_PERMISSION
} from "../../../Communication/communication-permissions.ts"
import { lazyPage } from "../lazyPage.tsx"

const CommunicationHome = lazyPage(() =>
  import("../../../Communication/CommunicationLayout.tsx").then((m) => ({
    default: m.CommunicationHome
  }))
)
const FirstSettingsPage = lazyPage(() =>
  import("../../../Communication/settings/CommunicationSettingsLayout.tsx").then(
    (m) => ({ default: m.FirstSettingsPage })
  )
)
const CommunicationLayout = lazy(
  () => import("../../../Communication/CommunicationLayout.tsx")
)
const CommunicationSettingsLayout = lazy(
  () =>
    import("../../../Communication/settings/CommunicationSettingsLayout.tsx")
)
const EmailSettingsPage = lazy(
  () => import("../../../Communication/settings/EmailSettingsPage.tsx")
)
const SendingRulesPage = lazy(
  () => import("../../../Communication/settings/SendingRulesPage.tsx")
)
const SmsSettingsPage = lazy(
  () => import("../../../Communication/settings/SmsSettingsPage.tsx")
)
const TemplatesPage = lazy(
  () => import("../../../Communication/settings/templates/TemplatesPage.tsx")
)

const OverviewPage = lazy(
  () => import("../../../Communication/overview/OverviewPage.tsx")
)
const OptOutsPage = lazy(
  () => import("../../../Communication/consent/OptOutsPage.tsx")
)
const CampaignsPage = lazy(
  () => import("../../../Communication/campaigns/CampaignsPage.tsx")
)
const CampaignReportPage = lazy(
  () => import("../../../Communication/campaigns/CampaignReportPage.tsx")
)
const AutomationsPage = lazy(
  () => import("../../../Communication/automations/AutomationsPage.tsx")
)
const MessagesPage = lazy(
  () => import("../../../Communication/messages/MessagesPage.tsx")
)

const readsMessages = (page: ReactNode) => (
  <PermissionGate
    mode="some"
    paths={[COMMUNICATION_SEND_PERMISSION, COMMUNICATION_VIEW_ALL_PERMISSION]}
  >
    <Suspense fallback={loading}>{page}</Suspense>
  </PermissionGate>
)

const readsCampaigns = (page: ReactNode) => (
  <PermissionGate
    mode="some"
    paths={[
      COMMUNICATION_BULK_SEND_PERMISSION,
      COMMUNICATION_VIEW_ALL_PERMISSION
    ]}
  >
    <Suspense fallback={loading}>{page}</Suspense>
  </PermissionGate>
)

const loading = <Skeleton active paragraph={{ rows: 6 }} />

const gated = (path: PermissionPath, page: ReactNode) => (
  <PermissionGate paths={[path]}>
    <Suspense fallback={loading}>{page}</Suspense>
  </PermissionGate>
)

export const communicationRoutes: RouteObject[] = [
  {
    path: RoutesPaths.communication.root,
    element: (
      <PermissionGate pages={["communication"]}>
        <Suspense fallback={loading}>
          <CommunicationLayout />
        </Suspense>
      </PermissionGate>
    ),
    children: [
      {
        index: true,
        element: <CommunicationHome fallback={<FirstSettingsPage />} />
      },
      {
        path: RoutesPaths.communication.overview,
        element: readsMessages(<OverviewPage />)
      },
      {
        path: RoutesPaths.communication.messages,
        element: readsMessages(<MessagesPage />)
      },
      {
        path: `${RoutesPaths.communication.messages}/:messageId`,
        element: readsMessages(<MessagesPage />)
      },
      {
        path: RoutesPaths.communication.optOuts,
        element: gated(COMMUNICATION_VIEW_ALL_PERMISSION, <OptOutsPage />)
      },
      {
        path: RoutesPaths.communication.campaigns,
        element: readsCampaigns(<CampaignsPage />)
      },
      {
        path: `${RoutesPaths.communication.campaigns}/:campaignId`,
        element: readsCampaigns(<CampaignReportPage />)
      },
      {
        path: RoutesPaths.communication.automations,
        element: gated(COMMUNICATION_SETTINGS_PERMISSION, <AutomationsPage />)
      },
      {
        path: RoutesPaths.communication.settings,
        element: (
          <PermissionGate
            mode="some"
            paths={[
              COMMUNICATION_SETTINGS_PERMISSION,
              COMMUNICATION_TEMPLATES_PERMISSION
            ]}
          >
            <Suspense fallback={loading}>
              <CommunicationSettingsLayout />
            </Suspense>
          </PermissionGate>
        ),
        children: [
          { index: true, element: <FirstSettingsPage /> },
          {
            path: RoutesPaths.communication.settingsEmail,
            element: gated(
              COMMUNICATION_SETTINGS_PERMISSION,
              <EmailSettingsPage />
            )
          },
          {
            path: RoutesPaths.communication.settingsSendingRules,
            element: gated(
              COMMUNICATION_SETTINGS_PERMISSION,
              <SendingRulesPage />
            )
          },
          {
            path: RoutesPaths.communication.settingsSms,
            element: gated(
              COMMUNICATION_SETTINGS_PERMISSION,
              <SmsSettingsPage />
            )
          },
          {
            path: RoutesPaths.communication.settingsTemplates,
            element: gated(
              COMMUNICATION_TEMPLATES_PERMISSION,
              <TemplatesPage />
            )
          }
        ]
      }
    ]
  }
]
